import React, { useEffect, Suspense, useRef, useState } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigationType } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import theme from './theme';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ChatPanelProvider } from './contexts/ChatPanelContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { GoogleMapsProvider } from './contexts/GoogleMapsProvider';
import ProtectedRoute from './components/auth/ProtectedRoute';
import AdminRoute from './components/auth/AdminRoute';
import SuperAdminRoute from './components/auth/SuperAdminRoute';
import MultiModalLayout from './components/MultiModalLayout';
import CookieConsentBanner from './components/CookieConsentBanner';
import { initializePreloading, preloadBasedOnRoute } from './utils/preloader';
import { initGA, setAnalyticsConsent, trackPageView } from './utils/analytics';
import { applyRouteSeo } from './utils/seo';
import { canUseAnalyticsCookies, COOKIE_CONSENT_EVENT } from './utils/cookieConsent';
import { isAdmin, isAgent, isLawyer, getPendingProfessionalRole } from './constants/roles';

// ============================================================================
// EAGER LOADED COMPONENTS (Critical for initial page load)
// ============================================================================
import Home from './pages/Home';
import Login from './pages/Login';
import Signup from './pages/Signup';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import { tr } from 'date-fns/locale';

// ============================================================================
// LAZY LOADED COMPONENTS (Code Splitting)
// ============================================================================
// Heavy dashboard and core app pages
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const AgentDashboard = React.lazy(() => import('./components/agent/AgentDashboard'));
const AgentSettings = React.lazy(() => import('./components/agent/AgentSettings'));
const LawyerDashboard = React.lazy(() => import('./components/lawyer/LawyerDashboard'));
const PendingProfessionalDashboard = React.lazy(() => import('./components/PendingProfessionalDashboard'));
const Profile = React.lazy(() => import('./pages/Profile'));

// Property-related pages (heaviest components due to maps/data)
const FindPropertiesPage = React.lazy(() => import('./pages/FindPropertiesPage'));
const FindPropertiesMap = React.lazy(() => import('./pages/FindPropertiesMap'));
const PropertyDetail = React.lazy(() => import('./pages/PropertyDetail'));
const MyPropertyPage = React.lazy(() => import('./pages/MyPropertyPage'));
const MyPropertyDashboardPage = React.lazy(() => import('./pages/MyPropertyDashboardPage'));
const MyPropertiesPage = React.lazy(() => import('./pages/MyPropertiesPage'));
const PropertySellingDashboard = React.lazy(() => import('./components/property/SellingJourney/PropertySellingDashboard'));
const SimilarProperties = React.lazy(() => import('./pages/SimilarProperties'));
const MarketingStudio = React.lazy(() => import('./pages/MarketingStudio'));
const PublicPropertyMarketingPage = React.lazy(() => import('./pages/PublicPropertyMarketingPage'));
const NotFoundPage = React.lazy(() => import('./pages/NotFoundPage'));

// Chat and communication features
const ChatPage = React.lazy(() => import('./pages/ChatPage'));
const SchedulePage = React.lazy(() => import('./pages/SchedulePage'));

// Admin and utility pages
const AdminDashboard = React.lazy(() => import('./pages/AdminDashboard'));
const SettingsPage = React.lazy(() => import('./pages/SettingsPage'));
const RBACManagement = React.lazy(() => import('./pages/RBACManagement'));
const AdminUsersPage = React.lazy(() => import('./pages/AdminUsersPage'));
const AdminUserPropertiesPage = React.lazy(() => import('./pages/AdminUserPropertiesPage'));
const AdminConsultationsPage = React.lazy(() => import('./pages/AdminConsultationsPage'));
const AdminConversationFeedbackPage = React.lazy(() => import('./pages/AdminConversationFeedbackPage'));
const AdminMarketingTargetsPage = React.lazy(() => import('./pages/AdminMarketingTargetsPage'));
const AdminOutreachCampaignsPage = React.lazy(() => import('./pages/AdminOutreachCampaignsPage'));
const AdminPlatformMarketingQueuePage = React.lazy(() => import('./pages/AdminPlatformMarketingQueuePage'));
const AdminBlogPage = React.lazy(() => import('./pages/AdminBlogPage'));
const AllHistoryPage = React.lazy(() => import('./pages/AllHistoryPage'));
const PromptsPage = React.lazy(() => import('./pages/PromptsPage'));
const HistoryPage = React.lazy(() => import('./pages/HistoryPage'));
const MetricsPage = React.lazy(() => import('./pages/MetricsPage'));
const LogsPage = React.lazy(() => import('./pages/LogsPage'));
const AdminCMA = React.lazy(() => import('./pages/AdminCMA'));
const NotificationsPage = React.lazy(() => import('./pages/NotificationsPage'));
const ActivityPage = React.lazy(() => import('./pages/ActivityPage'));

// Static/info pages
const About = React.lazy(() => import('./pages/About'));
const MarketPage = React.lazy(() => import('./pages/MarketPage'));
const FAQPage = React.lazy(() => import('./pages/FAQPage'));
const BlogListPage = React.lazy(() => import('./pages/BlogListPage'));
const BlogPostPage = React.lazy(() => import('./pages/BlogPostPage'));
const ContactPage = React.lazy(() => import('./pages/ContactPage'));
const PrivacyPolicyPage = React.lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsOfServicePage = React.lazy(() => import('./pages/TermsOfServicePage'));
const OAuthCallback = React.lazy(() => import('./pages/OAuthCallback'));
const ServiceUnavailablePage = React.lazy(() => import('./pages/ServiceUnavailablePage'));
const OutreachLandingPage = React.lazy(() => import('./pages/OutreachLandingPage'));

// ============================================================================
// LOADING COMPONENT
// ============================================================================
const PageLoadingSpinner = ({ message = "Loading..." }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '60vh',
      gap: 2,
    }}
  >
    <CircularProgress 
      size={40} 
      thickness={4}
      sx={{ 
        color: theme => theme.palette.primary.main,
      }}
    />
    <Typography 
      variant="body1" 
      color="text.secondary"
      sx={{ fontWeight: 500 }}
    >
      {message}
    </Typography>
  </Box>
);

// ============================================================================
// SUSPENSE WRAPPER COMPONENTS
// ============================================================================
const SuspenseWrapper = ({ children, fallback }) => (
  <Suspense fallback={fallback || <PageLoadingSpinner />}>
    {children}
  </Suspense>
);

const ProtectedSuspenseRoute = ({ children, fallback }) => (
  <ProtectedRoute>
    <SuspenseWrapper fallback={fallback}>
      {children}
    </SuspenseWrapper>
  </ProtectedRoute>
);

const MultiModalSuspenseLayout = ({ children, fallback, layoutProps }) => (
  <MultiModalLayout {...layoutProps}>
    <SuspenseWrapper fallback={fallback}>
      {children}
    </SuspenseWrapper>
  </MultiModalLayout>
);

const ProtectedMultiModalSuspenseRoute = ({ children, fallback }) => (
  <ProtectedRoute>
    <MultiModalLayout>
      <SuspenseWrapper fallback={fallback}>
        {children}
      </SuspenseWrapper>
    </MultiModalLayout>
  </ProtectedRoute>
);

// Component for preloading logic that needs both AuthContext and Router context
function PreloadingManager() {
  const { currentUser } = useAuth();
  const location = useLocation();
  
  // Initialize preloading strategies
  useEffect(() => {
    initializePreloading(location.pathname, !!currentUser);
  }, [location.pathname, currentUser]);

  // Preload based on route changes
  useEffect(() => {
    preloadBasedOnRoute(location.pathname);
  }, [location.pathname]);

  return null; // This component doesn't render anything
}

// Google Analytics route tracker — sends page_view on every navigation
function RouteTracker() {
  const location = useLocation();
  const [canTrackAnalytics, setCanTrackAnalytics] = useState(canUseAnalyticsCookies());

  useEffect(() => {
    const updateConsentState = () => {
      setCanTrackAnalytics(canUseAnalyticsCookies());
    };

    window.addEventListener(COOKIE_CONSENT_EVENT, updateConsentState);
    window.addEventListener('storage', updateConsentState);

    return () => {
      window.removeEventListener(COOKIE_CONSENT_EVENT, updateConsentState);
      window.removeEventListener('storage', updateConsentState);
    };
  }, []);

  // Initialize GA4 once on mount
  useEffect(() => {
    initGA();
  }, []);

  // Keep GA consent mode in sync with cookie preferences
  useEffect(() => {
    // setAnalyticsConsent(canTrackAnalytics);
    setAnalyticsConsent(true);
  }, [canTrackAnalytics]);

  // Track page view on every route change
  useEffect(() => {
    applyRouteSeo(location.pathname);

    // if (canTrackAnalytics) {
    trackPageView(location.pathname + location.search);
    // }
  }, [location, canTrackAnalytics]);

  return null;
}

function ScrollToTopOnNavigate() {
  const location = useLocation();
  const navigationType = useNavigationType();
  const previousLocationKeyRef = useRef(location.key);
  const savedScrollPositionsRef = useRef(new Map());

  useEffect(() => {
    const previousKey = previousLocationKeyRef.current;
    if (previousKey && previousKey !== location.key) {
      savedScrollPositionsRef.current.set(previousKey, {
        left: window.scrollX,
        top: window.scrollY,
      });

      // Keep memory bounded for long sessions.
      if (savedScrollPositionsRef.current.size > 300) {
        const oldestKey = savedScrollPositionsRef.current.keys().next().value;
        savedScrollPositionsRef.current.delete(oldestKey);
      }
    }

    if (location.hash) {
      const element = document.getElementById(location.hash.slice(1));
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        previousLocationKeyRef.current = location.key;
        return;
      }
    }

    if (navigationType === 'POP') {
      const savedPosition = savedScrollPositionsRef.current.get(location.key);
      if (savedPosition) {
        window.scrollTo({
          left: savedPosition.left,
          top: savedPosition.top,
          behavior: 'auto',
        });
      }

      previousLocationKeyRef.current = location.key;
      return;
    }

    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    previousLocationKeyRef.current = location.key;
  }, [location, navigationType]);

  return null;
}

function UnifiedDashboardEntry() {
  const { currentUser } = useAuth();

  if (isAdmin(currentUser)) {
    return <AdminDashboard />;
  }

  if (isAgent(currentUser)) {
    return (
      <MultiModalLayout>
        <AgentDashboard />
      </MultiModalLayout>
    );
  }

  if (isLawyer(currentUser)) {
    return (
      <MultiModalLayout>
        <LawyerDashboard />
      </MultiModalLayout>
    );
  }

  // Registered as agent/lawyer but not yet approved by KeyTa: show that
  // workspace in its pending state instead of the client dashboard.
  const pendingRole = getPendingProfessionalRole(currentUser);
  if (pendingRole) {
    return (
      <MultiModalLayout>
        <PendingProfessionalDashboard role={pendingRole} />
      </MultiModalLayout>
    );
  }

  return (
    <MultiModalLayout>
      <Dashboard />
    </MultiModalLayout>
  );
}

function LegacyDashboardRedirect() {
  const location = useLocation();

  return (
    <Navigate
      to={`/dashboard${location.search}${location.hash}`}
      replace
    />
  );
}

// Separate component for the app content that needs AuthContext
function AppContent() {
  const { logout } = useAuth();
  
  useEffect(() => {
    const handleAuthExpired = async () => {
      await logout();
      window.location.href = '/login';
    };

    window.addEventListener('auth:expired', handleAuthExpired);
    return () => window.removeEventListener('auth:expired', handleAuthExpired);
  }, [logout]);

  useEffect(() => {
    const handleServiceUnavailable = () => {
      if (window.location.pathname !== '/service-unavailable') {
        window.location.href = '/service-unavailable';
      }
    };

    window.addEventListener('service:unavailable', handleServiceUnavailable);
    return () => window.removeEventListener('service:unavailable', handleServiceUnavailable);
  }, []);

  return (
    <Router>
      <ChatPanelProvider>
        <PreloadingManager />
        <RouteTracker />
        <ScrollToTopOnNavigate />
            <Routes>
              {/* ========================================== */}
              {/* EAGER LOADED ROUTES (Critical paths) */}
              {/* ========================================== */}
              <Route path="/" element={<Home />} />
              <Route path="/login" element={<Login />} />
              {/* <Route path="/signup" element={<Signup />} /> */}
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/verify-email" element={<VerifyEmail />} />

              {/* Public outreach landing (QR / mail attribution) */}
              <Route
                path="/outreach/:code"
                element={
                  // <MultiModalSuspenseLayout
                  //   fallback={<PageLoadingSpinner message="Loading your personalized page..." />}
                  //   layoutProps={{ allowPageScroll: true }}
                  // >
                    <OutreachLandingPage />
                  // </MultiModalSuspenseLayout>
                }
              />

              <Route 
                path="/signup" 
                element={
                  <MultiModalSuspenseLayout 
                    fallback={<PageLoadingSpinner message="Loading signup..." />}
                    layoutProps={{ allowPageScroll: true }}
                  >
                    <Signup />
                  </MultiModalSuspenseLayout>
                } 
              />

              {/* ========================================== */}
              {/* LAZY LOADED ROUTES (Code Split) */}
              {/* ========================================== */}
              
              {/* Static/Info Pages */}
              <Route 
                path="/about" 
                element={
                  <SuspenseWrapper fallback={<PageLoadingSpinner message="Loading About..." />}>
                    <About />
                  </SuspenseWrapper>
                } 
              />
              <Route 
                path="/market" 
                element={
                  <SuspenseWrapper fallback={<PageLoadingSpinner message="Loading Market Analysis..." />}>
                    <MarketPage />
                  </SuspenseWrapper>
                } 
              />
              <Route 
                path="/faq" 
                element={
                  <SuspenseWrapper fallback={<PageLoadingSpinner message="Loading FAQ..." />}>
                    <FAQPage />
                  </SuspenseWrapper>
                } 
              />
              <Route
                path="/blog"
                element={
                  <SuspenseWrapper fallback={<PageLoadingSpinner message="Loading Blog..." />}>
                    <BlogListPage />
                  </SuspenseWrapper>
                }
              />
              <Route
                path="/blog/:slug"
                element={
                  <SuspenseWrapper fallback={<PageLoadingSpinner message="Loading Post..." />}>
                    <BlogPostPage />
                  </SuspenseWrapper>
                }
              />
              <Route 
                path="/contact" 
                element={
                  <SuspenseWrapper fallback={<PageLoadingSpinner message="Loading Contact..." />}>
                    <ContactPage />
                  </SuspenseWrapper>
                } 
              />
              <Route
                path="/privacy-policy"
                element={
                  <SuspenseWrapper fallback={<PageLoadingSpinner message="Loading Privacy Policy..." />}>
                    <PrivacyPolicyPage />
                  </SuspenseWrapper>
                }
              />
              <Route
                path="/terms-of-service"
                element={
                  <SuspenseWrapper fallback={<PageLoadingSpinner message="Loading Terms of Service..." />}>
                    <TermsOfServicePage />
                  </SuspenseWrapper>
                }
              />
              <Route 
                path="/auth/callback" 
                element={
                  <SuspenseWrapper fallback={<PageLoadingSpinner message="Processing authentication..." />}>
                    <OAuthCallback />
                  </SuspenseWrapper>
                } 
              />
              <Route
                path="/service-unavailable"
                element={
                  <SuspenseWrapper fallback={<PageLoadingSpinner message="Preparing status page..." />}>
                    <ServiceUnavailablePage />
                  </SuspenseWrapper>
                }
              />

              {/* Core App Pages */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedSuspenseRoute 
                    fallback={<PageLoadingSpinner message="Loading Dashboard..." />}
                  >
                    <UnifiedDashboardEntry />
                  </ProtectedSuspenseRoute>
                }
              />
              {/* <Route
                path="/agent-dashboard"
                element={
                  <ProtectedMultiModalSuspenseRoute 
                    fallback={<PageLoadingSpinner message="Redirecting to Dashboard..." />}
                  >
                    <LegacyDashboardRedirect />
                  </ProtectedMultiModalSuspenseRoute>
                }
              />
               <Route
                path="/lawyer-dashboard"
                element={
                  <ProtectedMultiModalSuspenseRoute 
                    fallback={<PageLoadingSpinner message="Redirecting to Dashboard..." />}
                  >
                    <LegacyDashboardRedirect />
                  </ProtectedMultiModalSuspenseRoute>
                }
              /> */}
              <Route
                path="/agent-settings"
                element={
                  <ProtectedMultiModalSuspenseRoute 
                    fallback={<PageLoadingSpinner message="Loading Agent Settings..." />}
                  >
                    <AgentSettings />
                  </ProtectedMultiModalSuspenseRoute>
                }
              />             
              <Route
                path="/profile"
                element={
                  <ProtectedMultiModalSuspenseRoute 
                    fallback={<PageLoadingSpinner message="Loading Profile..." />}
                  >
                    <Profile />
                  </ProtectedMultiModalSuspenseRoute>
                }
              />
              <Route
                path="/notifications"
                element={
                  <ProtectedSuspenseRoute
                    fallback={<PageLoadingSpinner message="Loading Notifications..." />}
                  >
                    <NotificationsPage />
                  </ProtectedSuspenseRoute>
                }
              />
              <Route
                path="/activity"
                element={
                  <ProtectedSuspenseRoute
                    fallback={<PageLoadingSpinner message="Loading Activity..." />}
                  >
                    <ActivityPage />
                  </ProtectedSuspenseRoute>
                }
              />

              {/* Chat Features */}
              <Route 
                path="/chat" 
                element={
                  <SuspenseWrapper fallback={<PageLoadingSpinner message="Loading Chat..." />}>
                    <ChatPage />
                  </SuspenseWrapper>
                } 
              />

              {/* Property Pages (Heaviest components) */}
              <Route 
                path="/properties" 
                element={
                  <MultiModalSuspenseLayout 
                    fallback={<PageLoadingSpinner message="Loading Properties..." />}
                  >
                    <FindPropertiesPage />
                  </MultiModalSuspenseLayout>
                } 
              />
              <Route 
                path="/properties-map" 
                element={
                  <MultiModalSuspenseLayout 
                    fallback={<PageLoadingSpinner message="Loading Property Map..." />}
                  >
                    <FindPropertiesMap />
                  </MultiModalSuspenseLayout>
                } 
              />
              <Route 
                path="/property/:listingKey" 
                element={
                  <MultiModalSuspenseLayout 
                    fallback={<PageLoadingSpinner message="Loading Property Details..." />}
                  >
                    <PropertyDetail />
                  </MultiModalSuspenseLayout>
                } 
              />
              <Route
                path="/:provinceSlug/:citySlug/:addressSlug/home/:publicCode"
                element={
                  <SuspenseWrapper fallback={<PageLoadingSpinner message="Loading Public Listing..." />}>
                    <PublicPropertyMarketingPage />
                  </SuspenseWrapper>
                }
              />
              <Route 
                path="/schedule" 
                element={
                  <SuspenseWrapper fallback={<PageLoadingSpinner message="Loading Schedule..." />}>
                    <SchedulePage />
                  </SuspenseWrapper>
                } 
              />

              {/* User Property Management */}
              <Route 
                path="/my-property/:propertyId" 
                element={
                  <ProtectedMultiModalSuspenseRoute 
                    fallback={<PageLoadingSpinner message="Loading Property Dashboard..." />}
                  >
                    <MyPropertyDashboardPage />
                  </ProtectedMultiModalSuspenseRoute>
                } 
              />
              <Route 
                path="/my-property-mls/:propertyId" 
                element={
                  <ProtectedMultiModalSuspenseRoute 
                    fallback={<PageLoadingSpinner message="Loading Property Form..." />}
                  >
                    <MyPropertyPage />
                  </ProtectedMultiModalSuspenseRoute>
                } 
              />
              <Route 
                path="/my-property/:propertyId/journey" 
                element={
                  <ProtectedMultiModalSuspenseRoute 
                    fallback={<PageLoadingSpinner message="Loading Selling Journey..." />}
                  >
                    <PropertySellingDashboard />
                  </ProtectedMultiModalSuspenseRoute>
                } 
              />
              <Route 
                path="/my-property/:propertyId/similar" 
                element={
                  <ProtectedMultiModalSuspenseRoute 
                    fallback={<PageLoadingSpinner message="Loading Similar Properties..." />}
                  >
                    <SimilarProperties />
                  </ProtectedMultiModalSuspenseRoute>
                } 
              />
              <Route 
                path="/my-property/:propertyId/marketing" 
                element={
                  <ProtectedMultiModalSuspenseRoute 
                    fallback={<PageLoadingSpinner message="Loading Marketing Studio..." />}
                  >
                    <MarketingStudio />
                  </ProtectedMultiModalSuspenseRoute>
                } 
              />
              <Route 
                path="/my-properties" 
                element={
                  <ProtectedMultiModalSuspenseRoute 
                    fallback={<PageLoadingSpinner message="Loading My Properties..." />}
                  >
                    <MyPropertiesPage />
                  </ProtectedMultiModalSuspenseRoute>
                } 
              />

              {/* Admin Pages */}
              <Route 
                path="/admin" 
                element={
                  <AdminRoute>
                    <LegacyDashboardRedirect />
                  </AdminRoute>
                } 
              />

              {/* Settings Page - Super Admin Only */}
              <Route 
                path="/admin/settings" 
                element={
                  <SuperAdminRoute>
                    <SuspenseWrapper fallback={<PageLoadingSpinner message="Loading Settings..." />}>
                      <SettingsPage />
                    </SuspenseWrapper>
                  </SuperAdminRoute>
                } 
              />
              
              {/* Admin CMA Explorer */}
              <Route 
                path="/admin/cma" 
                element={
                  <SuperAdminRoute>
                    <SuspenseWrapper fallback={<PageLoadingSpinner message="Loading Admin CMA..." />}>
                      <AdminCMA />
                    </SuspenseWrapper>
                  </SuperAdminRoute>
                } 
              />

              {/* RBAC Management Page - Super Admin Only */}
              <Route 
                path="/admin/rbac-management" 
                element={
                  <SuperAdminRoute>
                    <SuspenseWrapper fallback={<PageLoadingSpinner message="Loading RBAC Management..." />}>
                      <RBACManagement />
                    </SuspenseWrapper>
                  </SuperAdminRoute>
                } 
              />

              <Route
                path="/admin/users-management"
                element={
                  <SuperAdminRoute>
                    <SuspenseWrapper fallback={<PageLoadingSpinner message="Loading Users And Visitors..." />}>
                      <AdminUsersPage />
                    </SuspenseWrapper>
                  </SuperAdminRoute>
                }
              />

              <Route
                path="/admin/users-properties"
                element={
                  <SuperAdminRoute>
                    <SuspenseWrapper fallback={<PageLoadingSpinner message="Loading User Properties..." />}>
                      <AdminUserPropertiesPage />
                    </SuspenseWrapper>
                  </SuperAdminRoute>
                }
              />

              <Route
                path="/admin/consultations"
                element={
                  <SuperAdminRoute>
                    <SuspenseWrapper fallback={<PageLoadingSpinner message="Loading Booked Consultations..." />}>
                      <AdminConsultationsPage />
                    </SuspenseWrapper>
                  </SuperAdminRoute>
                }
              />

              <Route
                path="/admin/conversation-feedback"
                element={
                  <SuperAdminRoute>
                    <SuspenseWrapper fallback={<PageLoadingSpinner message="Loading Conversation Feedback..." />}>
                      <AdminConversationFeedbackPage />
                    </SuspenseWrapper>
                  </SuperAdminRoute>
                }
              />

              <Route
                path="/admin/marketing-targets"
                element={
                  <SuperAdminRoute>
                    <SuspenseWrapper fallback={<PageLoadingSpinner message="Loading Marketing Targets..." />}>
                      <AdminMarketingTargetsPage />
                    </SuspenseWrapper>
                  </SuperAdminRoute>
                }
              />

              <Route
                path="/admin/outreach-campaigns"
                element={
                  <SuperAdminRoute>
                    <SuspenseWrapper fallback={<PageLoadingSpinner message="Loading Outreach Campaigns..." />}>
                      <AdminOutreachCampaignsPage />
                    </SuspenseWrapper>
                  </SuperAdminRoute>
                }
              />

              <Route
                path="/admin/platform-marketing"
                element={
                  <SuperAdminRoute>
                    <SuspenseWrapper fallback={<PageLoadingSpinner message="Loading Platform Marketing Queue..." />}>
                      <AdminPlatformMarketingQueuePage />
                    </SuspenseWrapper>
                  </SuperAdminRoute>
                }
              />

              <Route
                path="/admin/blog"
                element={
                  <SuperAdminRoute>
                    <SuspenseWrapper fallback={<PageLoadingSpinner message="Loading Blog Manager..." />}>
                      <AdminBlogPage />
                    </SuspenseWrapper>
                  </SuperAdminRoute>
                }
              />

              <Route 
                path="/admin/history" 
                element={
                  <SuperAdminRoute>
                    <SuspenseWrapper fallback={<PageLoadingSpinner message="Loading History..." />}>
                      <AllHistoryPage />
                    </SuspenseWrapper>
                  </SuperAdminRoute>
                } 
              />
              <Route 
                path="/admin/history/:threadId" 
                element={
                  <SuperAdminRoute>
                    <SuspenseWrapper fallback={<PageLoadingSpinner message="Loading Thread History..." />}>
                      <HistoryPage />
                    </SuspenseWrapper>
                  </SuperAdminRoute>
                } 
              />
              <Route 
                path="/admin/prompts" 
                element={
                  <SuperAdminRoute>
                    <SuspenseWrapper fallback={<PageLoadingSpinner message="Loading Prompts..." />}>
                      <PromptsPage />
                    </SuspenseWrapper>
                  </SuperAdminRoute>
                } 
              />
              <Route 
                path="/admin/metrics" 
                element={
                  <SuperAdminRoute>
                    <SuspenseWrapper fallback={<PageLoadingSpinner message="Loading System Metrics..." />}>
                      <MetricsPage />
                    </SuspenseWrapper>
                  </SuperAdminRoute>
                } 
              />
              <Route 
                path="/admin/logs" 
                element={
                  <SuperAdminRoute>
                    <SuspenseWrapper fallback={<PageLoadingSpinner message="Loading System Logs..." />}>
                      <LogsPage />
                    </SuspenseWrapper>
                  </SuperAdminRoute>
                } 
              />

              <Route
                path="*"
                element={
                  <SuspenseWrapper fallback={<PageLoadingSpinner message="Loading page..." />}>
                    <NotFoundPage />
                  </SuspenseWrapper>
                }
              />
            </Routes>
            <CookieConsentBanner />
      </ChatPanelProvider>
    </Router>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <NotificationProvider>
        <AuthProvider>
          <GoogleMapsProvider>
            <AppContent />
          </GoogleMapsProvider>
        </AuthProvider>
      </NotificationProvider>
    </ThemeProvider>
  );
}

export default App;

